/* ---------------------------------------------------------------------
 *                           Import Packages
 * --------------------------------------------------------------------- */

// React
import React, { useState, useCallback } from "react";

// React Router
import { Outlet, redirect, useSubmit } from "react-router-dom";

// Images
import logo from "../../assets/images/logo1.png";

// MUI
import { Paper, Box, IconButton, GlobalStyles } from "@mui/material";

// Components
import Chat from "../chat/Chat";
import TopBar from "../topBar/TopBar";
import LogoutDialog from "./LogoutDialog";

// Utils
import { logoutUser } from "../../lib/service/userLogin";
import { isLoggedIn } from "../../lib/data/userData";

/* -------------------------------------------
 *              Loader & Action
 * -------------------------------------------*/

/**
 * 根组件的loader函数
 * @returns
 */
export async function loader() {
  if (!isLoggedIn()) {
    return redirect("/login");
  }
  return true;
}

/**
 * 根组件的action函数
 * @param {request} request 请求对象
 * @returns {object}
 */
export async function action({ request }) {
  try {
    const formData = await request.formData();
    const type = formData.get("type");

    if (type === "logout") {
      await logoutUser();
      return redirect("/login");
    }
  } catch (error) {
    console.log(error);
  }
}

/* ---------------------------------------------------------------------
 *                             Root Component
 * --------------------------------------------------------------------- */

/**
 * 定义根组件
 * @returns 根组件
 * @function Root
 */
export default function Root() {
  /**
   * 提交表单
   * @type {Function}
   */
  const submit = useSubmit();

  /* -------------------------------------------
   *                  AI Dialog
   * -------------------------------------------*/

  /**
   * 控制AI对话窗口的打开和关闭
   * @type {boolean}
   */
  const [isChatOpen, setIsChatOpen] = useState(true);

  /**
   * 切换AI对话窗口的打开和关闭状态
   * @function toggleChatOpen
   * @returns {void}
   */
  const toggleChatOpen = () => {
    setIsChatOpen((pre) => !pre);
  };

  /* -------------------------------------------
   *                Logout Dialog
   * -------------------------------------------*/

  /**
   * 退出登录对话框的打开和关闭状态
   * @type {boolean}
   */
  const [openLogoutDialog, setOpenLogoutDialog] = useState(false);

  /**
   * 处理退出登录对话框的打开和关闭
   * @returns {void}
   */
  const handleOpenLogoutDialog = useCallback(() => {
    setOpenLogoutDialog(true);
  }, []);

  /**
   * 处理关闭对话框
   * @param {object} e - 事件对象
   * @param {string} reason - 键盘事件
   */
  const handleCloseLogoutDialog = useCallback(
    (e, reason) => {
      if (!reason || reason === "escapeKeyDown" || reason === "backdropClick") {
        setOpenLogoutDialog(false);
      }
    },
    [setOpenLogoutDialog]
  );

  /**
   * 处理确认对话框退出登录
   * @return {void}
   */
  const handleConfirmLogout = useCallback(() => {
    setOpenLogoutDialog(false);
    submit(
      { type: "logout" },
      { method: "post", encType: "application/x-www-form-urlencoded" }
    );
  }, [submit]);

  /* ---------------------------------------------------------------------
   *                            Render Component
   * --------------------------------------------------------------------- */

  return (
    <>
      {/* 全局样式 */}
      <GlobalStyles
        styles={{
          "*": {
            margin: 0,
            padding: 0,
            boxSizing: "border-box",
          },
          "html, body, #root": {
            height: "100%",
            width: "100%",
            overflow: "hidden",
          },
          "::-webkit-scrollbar": {
            width: "6px",
            background: "transparent",
          },
          "::-webkit-scrollbar-thumb": {
            background: "#888",
            borderRadius: "3px",
            minHeight: "40px",
          },
          "::-webkit-scrollbar-track": {
            background: "transparent",
          },
        }}
      />
      
      <Box
        sx={{
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* 顶栏 */}
        <TopBar onOpenLogoutDialog={handleOpenLogoutDialog} />

        <Box
          sx={{
            flex: 1,
            paddingX: 3,
            paddingBottom: 3,
            display: "flex",
            overflow: "hidden",
            position: "relative",
          }}
        >
          {/* 主页面区 */}
          <Paper
            elevation={1}
            sx={{
              flex: 1,
              minWidth: 0,
              height: "100%",
              overflow: "auto",
              background: "#fff",
              border: "2px solid #eee",
              boxSizing: "border-box",
              borderRadius: "8px",
              marginRight: isChatOpen ? 2 : 0,
              transition: "all 0.3s",
            }}
          >
            <Outlet />
          </Paper>

          {/* AI对话框 */}
          {isChatOpen && <Chat toggleChatOpen={toggleChatOpen} />}

          {/* 开启AI对话框按钮 */}
          {!isChatOpen && (
            <IconButton
              onClick={toggleChatOpen}
              sx={{
                position: "absolute",
                bottom: 38,
                right: 38,
                width: 45,
                height: 45,
                padding: 0,
                borderRadius: "50%",
                boxShadow: 2,
                backgroundColor: "#fff",
                "&:hover": { backgroundColor: "#f0f0f0" },
              }}
            >
              <img
                src={logo}
                alt="打开聊天"
                style={{ width: "100%", height: "100%", borderRadius: "50%" }}
              />
            </IconButton>
          )}

          {/* 退出登录对话框 */}
          <LogoutDialog
            openLogoutDialog={openLogoutDialog}
            onCloseLogoutDialog={handleCloseLogoutDialog}
            onConfirmLogout={handleConfirmLogout}
          />
        </Box>
      </Box>
    </>
  );
}
